import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Login } from './components/Login'
import { ReviewPanel } from './components/ReviewPanel'
import { ClauseSearch } from './components/ClauseSearch'
import { RequisitesPanel } from './components/RequisitesPanel'
import { AssistantPanel } from './components/AssistantPanel'
import { UnsupportedNotice } from './components/UnsupportedNotice'
import { BrandLockup } from './brand'
import { ApiError, api, clearStoredToken, fetchCatalogRevision, restoreSession, streamCatalog } from './api'
import { catalogReconnectDelay, isCatalogStale } from './catalog'
import { CABINET_URL } from './env'
import { isWord14Supported } from './word'
import type { Finding, PaneMode } from './types'
import { LanguageSwitch, setLocale, t, useLocale, type Locale } from './i18n'

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  )
}

function errorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status
  if (err && typeof err === 'object' && 'status' in err) {
    const status = (err as { status?: unknown }).status
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}

async function syncLocale(auth: string): Promise<void> {
  try {
    const me = await api<{ ui_locale?: string | null }>('/me', auth)
    if (me.ui_locale === 'en' || me.ui_locale === 'uk') setLocale(me.ui_locale)
  } catch {
    // Keep the language chosen on this device.
  }
}

export default function App() {
  useLocale()
  const [token, setToken] = useState<string | null>(null)
  const [wordSupported] = useState<boolean>(() => isWord14Supported())
  const [mode, setMode] = useState<PaneMode>('review')
  const [findings, setFindings] = useState<Finding[]>([])
  const [liveRevision, setLiveRevision] = useState<string | null>(null)
  const [pinnedRevision, setPinnedRevision] = useState<string | null>(null)
  const [dismissedRevision, setDismissedRevision] = useState<string | null>(null)
  const [reviewRunning, setReviewRunning] = useState(false)
  const [rerunNonce, setRerunNonce] = useState(0)
  const [requisitesEpoch, setRequisitesEpoch] = useState(0)
  const liveRevisionRef = useRef<string | null>(null)
  useLayoutEffect(() => {
    liveRevisionRef.current = liveRevision
  }, [liveRevision])

  const logout = useCallback(() => {
    clearStoredToken()
    setToken(null)
    setFindings([])
    setLiveRevision(null)
    setPinnedRevision(null)
    setDismissedRevision(null)
    setReviewRunning(false)
    setRerunNonce(0)
  }, [])

  const onRunningChange = useCallback((running: boolean) => {
    setReviewRunning(running)
    if (running) {
      const live = liveRevisionRef.current
      if (live) setPinnedRevision(live)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void restoreSession().then((restored) => {
      if (cancelled || !restored) return
      setToken(restored)
      void syncLocale(restored)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    let attempt = 0
    const auth = token
    let controller = new AbortController()
    let sleepTimer: number | undefined

    function applyRevision(revision: string) {
      setLiveRevision(revision)
      setPinnedRevision((prev) => prev ?? revision)
    }

    async function pull() {
      try {
        const { revision } = await fetchCatalogRevision(auth)
        if (cancelled) return
        applyRevision(revision)
      } catch (err) {
        if (cancelled) return
        if (errorStatus(err) === 401) logout()
      }
    }

    async function listen() {
      while (!cancelled) {
        controller = new AbortController()
        try {
          await streamCatalog({
            token: auth,
            signal: controller.signal,
            onRevision: (revision) => {
              if (cancelled) return
              attempt = 0
              applyRevision(revision)
            },
          })
        } catch (err) {
          if (cancelled || isAbortError(err)) break
          if (errorStatus(err) === 401) {
            logout()
            return
          }
        }
        if (cancelled) break
        await pull()
        const delay = catalogReconnectDelay(attempt)
        attempt += 1
        await new Promise<void>((resolve) => {
          sleepTimer = window.setTimeout(resolve, delay)
        })
      }
    }

    void pull()
    void listen()

    function onVis() {
      if (document.visibilityState === 'visible') void pull()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onVis)
    return () => {
      cancelled = true
      controller.abort()
      if (sleepTimer !== undefined) window.clearTimeout(sleepTimer)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onVis)
    }
  }, [token, logout])

  if (!wordSupported) {
    return <UnsupportedNotice />
  }

  if (!token) {
    return (
      <Login
        onSuccess={(next) => {
          setToken(next)
          void syncLocale(next)
        }}
      />
    )
  }

  const showStale =
    !reviewRunning && isCatalogStale(pinnedRevision, liveRevision, dismissedRevision)

  function rerunReview() {
    setMode('review')
    setRerunNonce((n) => n + 1)
  }

  return (
    <main className="pane pane-app">
      <header className="pane-head">
        <h1>
          <BrandLockup compact />
        </h1>
        <div className="head-actions">
          <LanguageSwitch
            onPick={(next: Locale) => {
              if (!token) return
              void api('/me', token, {
                method: 'PATCH',
                body: JSON.stringify({ ui_locale: next }),
              })
                .catch(() => {
                  // The pane already switched. The next sign-in will retry the save.
                })
                .finally(() => setRequisitesEpoch((n) => n + 1))
            }}
          />
          <a href={CABINET_URL} target="_blank" rel="noreferrer">{t('Кабінет')}</a>
          <button type="button" className="linkish" onClick={logout}>{t('Вийти')}</button>
        </div>
      </header>
      <nav className="mode-tabs" aria-label={t('Режим панелі')} role="tablist">
        <button
          type="button"
          role="tab"
          className={mode === 'review' ? 'active' : ''}
          aria-selected={mode === 'review'}
          onClick={() => setMode('review')}
        >{t('Ревʼю')}</button>
        <button
          type="button"
          role="tab"
          className={mode === 'clauses' ? 'active' : ''}
          aria-selected={mode === 'clauses'}
          onClick={() => setMode('clauses')}
        >{t('Клаузи')}</button>
        <button
          type="button"
          role="tab"
          className={mode === 'requisites' ? 'active' : ''}
          aria-selected={mode === 'requisites'}
          onClick={() => setMode('requisites')}
        >{t('Реквізити')}</button>
        <button
          type="button"
          role="tab"
          className={mode === 'assistant' ? 'active' : ''}
          aria-selected={mode === 'assistant'}
          onClick={() => setMode('assistant')}
        >{t('Асистент')}</button>
      </nav>
      <div className="pane-body">
        <div hidden={mode !== 'review'}>
          <ReviewPanel
            token={token}
            onLogout={logout}
            onFindingsChange={setFindings}
            rerunNonce={rerunNonce}
            onRunningChange={onRunningChange}
          />
        </div>
        <div hidden={mode !== 'clauses'}>
          <ClauseSearch token={token} />
        </div>
        <div hidden={mode !== 'requisites'}>
          <RequisitesPanel token={token} refreshEpoch={requisitesEpoch} />
        </div>
        <div hidden={mode !== 'assistant'}>
          <AssistantPanel token={token} findings={findings} />
        </div>
      </div>
      {showStale
        ? createPortal(
            <div
              className="catalog-dialog-backdrop"
              onClick={() => setDismissedRevision(liveRevision)}
            >
              <div
                className="catalog-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="catalog-dialog-title"
                onClick={(e) => e.stopPropagation()}
              >
                <p id="catalog-dialog-title">
                  {t('Плейбук або реквізити оновлені в кабінеті. Поточний результат застарів.')}
                </p>
                <div className="actions">
                  <button type="button" onClick={rerunReview}>{t('Перевірити знову')}</button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setDismissedRevision(liveRevision)}
                  >{t('Сховати')}</button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </main>
  )
}
